import { apiClient } from '../../apiHelper';
import { useCreate, useDelete, useSingleFetch } from '../react-query';
import type { Role } from '../roles/roles';

const keys = {
  all: ['role-assignments'] as const,
  forUser: (uid: string) => ['role-assignments', uid] as const,
  effective: (uid: string) => ['effective-permissions', uid] as const,
  // Scope selector (`useMyScopes`) — must refresh when placements change so
  // the left-bar facility dropdown picks up newly assigned / revoked facilities.
  meFacilities: ['me-facilities'] as const,
};

/** Queries to invalidate after assign/revoke so UI scope + staff lists stay in sync. */
const assignmentMutationKeys = [keys.all, keys.meFacilities];

export interface RoleAssignment {
  user_id: string;
  role_id: string;
  org_unit_id: string;
  created_at: string;
}

export interface EffectivePermissions {
  user_id: string;
  roles: Role[];
  facilities: string[];
  permissions: Record<string, boolean>;
}

export function useUserAssignments(uid: string | undefined, config = {}) {
  const fn = async (): Promise<RoleAssignment[]> => {
    if (!uid) return [];
    const res = await apiClient.get(`users/${uid}/role-assignments`);
    return (res?.data ?? res) as RoleAssignment[];
  };
  return useSingleFetch(fn, uid ? keys.forUser(uid) : null, uid ?? '', config);
}

export function useEffectivePermissions(uid: string | undefined, config = {}) {
  const fn = async (): Promise<EffectivePermissions | null> => {
    if (!uid) return null;
    const res = await apiClient.get(`users/${uid}/effective-permissions`);
    return (res?.data ?? res) as EffectivePermissions;
  };
  return useSingleFetch(fn, uid ? keys.effective(uid) : null, uid ?? '', config);
}

export function useAssignRole(
  uid: string,
  onSuccess?: (response: any, data: { role_id: string; org_unit_id: string }) => void
) {
  const fn = async (data: { role_id: string; org_unit_id: string }) => {
    const res = await apiClient.post(`users/${uid}/role-assignments`, data);
    return res?.data ?? res;
  };
  return useCreate(
    fn,
    assignmentMutationKeys,
    'Error while assigning role',
    'Role assigned',
    onSuccess
  );
}

export function useRevokeRole(uid: string) {
  const fn = async (data: { role_id: string; org_unit_id: string }) => {
    await apiClient.delete(
      `users/${uid}/role-assignments?role_id=${data.role_id}&org_unit_id=${data.org_unit_id}`
    );
  };
  return useDelete(
    fn,
    assignmentMutationKeys,
    'Error while revoking role',
    'Role revoked'
  );
}
